// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Alarm } from '../../../../types/alarm';
import { alarmLine } from '../../../../utils/powerFlowAlarms';
import { alarmDetailPath } from '../../../../services/alarmSelectors';

/** Which breaker's ALARM mark was activated, and where it sits on the stage. */
export interface BreakerAlarmTarget {
  ids: string[];
  x: number;
  y: number;
  horizontal?: boolean;
}

export interface BreakerAlarmPopoverProps {
  target: BreakerAlarmTarget;
  /** The alarm service's open alarms on the breaker. */
  alarms: Alarm[];
  nameOf: (_id: string) => string;
  onClose: () => void;
}

/**
 * What the breaker's ALARM mark stands for, one entry per open alarm, each
 * linking to the alarm detail page the Home list opens. A breaker whose
 * `AlarmStatus` reading is raised but has no record in the alarm service says
 * so rather than showing nothing — the telemetry flag and the alarm row are
 * two different sources and only one of them may be wired.
 *
 * Escape and a press anywhere else close it, as the notices popover does.
 */
export const BreakerAlarmPopover = ({
  target,
  alarms,
  nameOf,
  onClose,
}: BreakerAlarmPopoverProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [onClose]);

  const name = target.ids.map(nameOf).join(', ');
  return (
    <div
      ref={rootRef}
      className="sld-alarm-popover"
      role="dialog"
      aria-label={`Alarms on ${name}`}
      // Beside the ALARM word on a vertical run, below the glyph on a horizontal one.
      style={
        target.horizontal
          ? { left: target.x - 40, top: target.y + 28 }
          : { left: target.x + 64, top: target.y - 12 }
      }
      onPointerDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
      data-testid="breaker-alarm-popover"
    >
      <p className="sld-alarm-popover__title">
        <span>{name}</span>
        <button
          type="button"
          className="sld-alarm-popover__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </p>
      {alarms.length === 0 ? (
        <p className="sld-alarm-popover__empty">
          The breaker reports an alarm status, but the alarm service has no open alarm for it.
        </p>
      ) : (
        <ul>
          {alarms.map(alarm => (
            <li key={alarm.uid}>
              <Link to={alarmDetailPath(alarm)}>{alarmLine(alarm)}</Link>
              {alarm.summary && alarm.summary !== alarm.ruleTitle && (
                <span className="sld-alarm-popover__summary">{alarm.summary}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
